import React from 'react';
import { BlockMath, InlineMath } from 'react-katex';

// Helper function to format mathematical expressions
export const formatMathExpression = (text: string): string => {
  let result = text;

  // First, convert Unicode superscripts to regular format before processing
  // Unicode superscripts to regular: 2²⁰² -> 2^202, 2¹⁰¹ -> 2^101, etc.
  result = result.replace(/(\d)([²³⁴⁵⁶⁷⁸⁹⁰¹]+)/g, (match, base, sup) => {
    const superscriptMap: {[key: string]: string} = {'²':'2','³':'3','⁴':'4','⁵':'5','⁶':'6','⁷':'7','⁸':'8','⁹':'9','⁰':'0','¹':'1'};
    const exponent = sup.split('').map((c: string) => superscriptMap[c] || c).join('');
    return `${base}^{${exponent}}`;
  });

  // Convert Unicode subscripts to LaTeX subscripts: a₁ -> a_1, aₖ -> a_k
  result = result.replace(/([a-zA-Z])([₀₁₂₃₄₅₆₇₈₉ₖ]+)/g, (match, base, sub) => {
    const subscriptMap: {[key: string]: string} = {'ₖ':'k','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9','₀':'0'};
    const subscript = sub.split('').map((c: string) => subscriptMap[c] || c).join('');
    return `${base}_{${subscript}}`;
  });

  // Now apply LaTeX formatting
  return result
    // Complex fractions with parentheses: (2^289 + 1)/(2^17 + 1) -> \frac{2^{289} + 1}{2^{17} + 1}
    .replace(/\(([^)]+)\)\/\(([^)]+)\)/g, (match, num, den) => {
      // Process numerator and denominator separately for exponents
      const processedNum = num.replace(/(\d+)\^(\d+)/g, '$1^{$2}')
                              .replace(/([a-zA-Z])\^([a-zA-Z0-9]+)/g, '$1^{$2}');
      const processedDen = den.replace(/(\d+)\^(\d+)/g, '$1^{$2}')
                              .replace(/([a-zA-Z])\^([a-zA-Z0-9]+)/g, '$1^{$2}');
      return `\\frac{${processedNum}}{${processedDen}}`;
    })
    // Floor functions: ⌊998/n⌋ -> \lfloor\frac{998}{n}\rfloor
    .replace(/⌊(\d+)\/([a-zA-Z])⌋/g, '\\lfloor\\frac{$1}{$2}\\rfloor')
    // Fractions with variables: 998/n -> \frac{998}{n}
    .replace(/(\d+)\/([a-zA-Z])/g, '\\frac{$1}{$2}')
    // Variables over numbers: x/2020 -> \frac{x}{2020}
    .replace(/([a-zA-Z])\/(\d+)/g, '\\frac{$1}{$2}')
    // Variable over variable: a/b -> \frac{a}{b}
    .replace(/([a-zA-Z])\/([a-zA-Z])/g, '\\frac{$1}{$2}')
    // Fractions with superscripts: 1/20^{20} -> \frac{1}{20^{20}}
    .replace(/(\d+)\/(\d+\^\{[^}]+\})/g, '\\frac{$1}{$2}')
    // Fractions with factorials: 1/2! -> \frac{1}{2!}
    .replace(/(\d+)\/(\d+!)/g, '\\frac{$1}{$2}')
    // Regular fractions: 1/2 -> \frac{1}{2}, 15/32 -> \frac{15}{32}
    .replace(/(\d+)\/(\d+)/g, '\\frac{$1}{$2}')
    // Exponents with numbers: 2^289 -> 2^{289}, 2^17 -> 2^{17}
    .replace(/(\d+)\^(\d+)/g, '$1^{$2}')
    // Exponents with variables that have subscripts: 2^a_1 -> 2^{a_1}, 2^a_k -> 2^{a_k}
    .replace(/(\d+)\^([a-zA-Z]_\{[^}]+\})/g, '$1^{$2}')
    // Exponents with simple variables: 2^a1 -> 2^{a1}, x^2 -> x^{2}
    .replace(/(\d+)\^([a-zA-Z][a-zA-Z0-9]*)/g, '$1^{$2}')
    .replace(/([a-zA-Z])\^([a-zA-Z0-9]+)/g, '$1^{$2}')
    // Square roots with parentheses: √(expression) -> \sqrt{(expression)}
    .replace(/√(\([^)]+\))/g, '\\sqrt{$1}')
    // Square roots: √n -> \sqrt{n}
    .replace(/√(\d+)/g, '\\sqrt{$1}')
    // Square roots: √variable -> \sqrt{variable}
    .replace(/√([a-zA-Z])/g, '\\sqrt{$1}')
    // Floor and ceiling functions
    .replace(/⌊/g, '\\lfloor ')
    .replace(/⌋/g, '\\rfloor')
    .replace(/⌈/g, '\\lceil ')
    .replace(/⌉/g, '\\rceil')
    // Ellipsis symbols
    .replace(/⋯/g, '\\cdots')
    .replace(/\.\.\./g, '\\ldots')
    // Special symbols
    .replace(/≤/g, '\\leq')
    .replace(/≥/g, '\\geq')
    .replace(/≠/g, '\\neq')
    .replace(/−/g, '-')
    .replace(/∑/g, '\\sum');
};

// Helper function to detect if text contains mathematical expressions that need formatting
export const containsMathExpressions = (text: string): boolean => {
  return /\d+\/\d+/.test(text) || // Regular fractions: 3/4
         /\d+\/[a-zA-Z]/.test(text) || // Fractions with variables: 998/n
         /[a-zA-Z]\/\d+/.test(text) || // Variables over numbers: x/2020
         /\d+\/\d+[²³⁴⁵⁶⁷⁸⁹⁰¹]+/.test(text) || // Fractions with superscripts: 1/20²⁰
         /\([^)]+\)\/\([^)]+\)/.test(text) || // Complex fractions: (2²⁸⁹ + 1)/(2¹⁷ + 1)
         /[²³⁴⁵⁶⁷⁸⁹⁰¹]+/.test(text) || // Unicode superscripts
         /[₀₁₂₃₄₅₆₇₈₉ₖ]+/.test(text) || // Unicode subscripts
         /[⌊⌋⌈⌉]/.test(text) || // Floor/ceiling brackets
         /[≤≥≠]/.test(text) || // Comparison symbols
         /⋯/.test(text) || // Ellipsis
         /√\d+/.test(text) || // Square roots with numbers
         /√\([^)]+\)/.test(text) || // Square roots with parentheses
         /[a-zA-Z]\/[a-zA-Z]/.test(text); // Variable fractions like a/b
};

// Keep the old function name for backward compatibility
export const containsFractions = containsMathExpressions;

// Helper function to render math expressions
export const renderMathContent = (content: string) => {
  // Handle special case for sets problem (like problem 8)
  if (content.includes('Consider the following 100 sets of 10 elements each:')) {
    return (
      <div>
        <div style={{ marginBottom: '15px' }}>
          Consider the following 100 sets of 10 elements each:
        </div>

        <div style={{ textAlign: 'center', margin: '20px 0', background: '#f8fafc', padding: '20px', borderRadius: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
            <div><BlockMath math="\{1, 2, 3, \ldots, 10\}" /></div>
            <div><BlockMath math="\{11, 12, 13, \ldots, 20\}" /></div>
            <div><BlockMath math="\{21, 22, 23, \ldots, 30\}" /></div>
            <div style={{ margin: '10px 0', fontSize: '20px' }}>⋮</div>
            <div><BlockMath math="\{991, 992, 993, \ldots, 1000\}" /></div>
          </div>
        </div>

        <div style={{ marginTop: '15px', fontWeight: '600' }}>
          How many of these sets contain exactly two multiples of 7?
        </div>
      </div>
    );
  }

  // Handle special case for systems of equations (like problem 18)
  if (content.includes('a₁x + b₁y + c₁z = 0; a₂x + b₂y + c₂z = 0; a₃x + b₃y + c₃z = 0')) {
    const beforeEquations = "Consider systems of three linear equations with unknowns x, y, and z,";
    const afterEquations = "where each of the coefficients is either 0 or 1 and the system has a solution other than x = y = z = 0.";

    const exampleMatch = content.match(/For example, one such system is \{([^}]+)\} with a nonzero solution of \{([^}]+)\}/);
    const exampleSystem = exampleMatch ? exampleMatch[1] : "";

    const questionMatch = content.match(/How many such systems are there\?[^?]*\?/);
    const question = questionMatch ? questionMatch[0] : "";

    return (
      <div>
        <div style={{ marginBottom: '15px' }}>{beforeEquations}</div>
        <div style={{ textAlign: 'center', margin: '20px 0', background: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
          <div style={{ marginBottom: '8px' }}><BlockMath math="a_1x + b_1y + c_1z = 0" /></div>
          <div style={{ marginBottom: '8px' }}><BlockMath math="a_2x + b_2y + c_2z = 0" /></div>
          <div><BlockMath math="a_3x + b_3y + c_3z = 0" /></div>
        </div>
        <div style={{ marginBottom: '15px' }}>{afterEquations}</div>
        {exampleSystem && (
          <div style={{ marginBottom: '15px', padding: '10px', background: '#f0f9ff', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
            <strong>Example:</strong> One such system is:
            <div style={{ margin: '8px 0', fontFamily: 'monospace' }}>
              {exampleSystem.split(', ').map((eq, i) => (
                <div key={i} style={{ margin: '2px 0' }}>{eq}</div>
              ))}
            </div>
            with a nonzero solution of <BlockMath math="\{x, y, z\} = \{1, -1, 1\}" />
          </div>
        )}
        <div style={{ marginTop: '15px', fontWeight: '600' }}>{question}</div>
      </div>
    );
  }

  // Process content line by line for general math formatting
  const lines = content.split('\n');

  return lines.map((line, index) => {
    // Special handling for system of equations (like "x + 3y = 3, ||x| - |y|| = 1")
    if (line.includes('system of equations') && line.includes('=')) {
      // Split the text to separate the descriptive part from the equations
      const systemMatch = line.match(/(.*system of equations[^?]*\?\s*)(.*)/);
      if (systemMatch) {
        const [, textPart, equationsPart] = systemMatch;

        // Split equations by comma and render each as BlockMath
        const equations = equationsPart.split(/,\s*/);
        const parts: (string | React.ReactElement)[] = [];

        if (textPart.trim()) {
          parts.push(textPart.trim());
        }

        equations.forEach((eq, i) => {
          if (eq.trim()) {
            const formattedEq = formatMathExpression(eq.trim());
            parts.push(<BlockMath key={`eq-${index}-${i}`} math={formattedEq} />);
          }
        });

        return (
          <div key={index} style={{ margin: '5px 0' }}>
            {parts}
          </div>
        );
      }
    }

    // Special handling for expressions with square roots and equals
    if (line.includes('√(') && line.includes('=')) {
      // Extract the complete mathematical expression
      const mathMatch = line.match(/(.*?)(√\([^)]+\)\s*[+-]\s*√\([^)]+\)\s*=\s*\d+)(.*)/);
      if (mathMatch) {
        const [, beforeText, mathExpr, afterText] = mathMatch;
        const parts: (string | React.ReactElement)[] = [];

        if (beforeText.trim()) parts.push(beforeText.trim());

        const formattedMath = formatMathExpression(mathExpr);
        parts.push(<BlockMath key={`math-${index}`} math={formattedMath} />);

        if (afterText.trim()) parts.push(afterText.trim());

        return (
          <div key={index} style={{ margin: '5px 0' }}>
            {parts}
          </div>
        );
      }
    }

    // Special handling for square roots that span multiple words like √(49 - x²) - fallback
    const squareRootPattern = /√\([^)]+\)/g;

    if (squareRootPattern.test(line)) {
      const parts: (string | React.ReactElement)[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      // Reset the regex
      squareRootPattern.lastIndex = 0;

      while ((match = squareRootPattern.exec(line)) !== null) {
        const currentMatchIndex = match.index;

        // Add text before the match
        if (currentMatchIndex > lastIndex) {
          const textBefore = line.substring(lastIndex, currentMatchIndex);
          // Process text before for other math expressions
          const beforeWords = textBefore.split(/(\s+)/);
          beforeWords.forEach((word, j) => {
            if (word.match(/^\s+$/)) {
              parts.push(word);
            } else if (containsFractions(word)) {
              const mathWord = formatMathExpression(word);
              parts.push(<InlineMath key={`before-sqrt-${index}-${currentMatchIndex}-${j}`} math={mathWord} />);
            } else {
              parts.push(word);
            }
          });
        }

        // Add the formatted square root expression
        const mathExpr = formatMathExpression(match[0]);
        parts.push(<BlockMath key={`sqrt-${index}-${currentMatchIndex}`} math={mathExpr} />);

        lastIndex = currentMatchIndex + match[0].length;
      }

      // Add any remaining text
      if (lastIndex < line.length) {
        const remainingText = line.substring(lastIndex);
        // Process the remaining text word by word for other math expressions
        const remainingWords = remainingText.split(/(\s+)/);
        remainingWords.forEach((word, j) => {
          if (word.match(/^\s+$/)) {
            parts.push(word);
          } else if (containsFractions(word)) {
            const mathWord = formatMathExpression(word);
            parts.push(<InlineMath key={`remaining-sqrt-${index}-${j}`} math={mathWord} />);
          } else {
            parts.push(word);
          }
        });
      }

      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {parts}
        </div>
      );
    }

    // Check if line contains ANY mathematical expressions that need formatting
    if (containsFractions(line)) {
      // Special handling for complex fractions like (2^289 + 1)/(2^17 + 1)
      const complexFractionPattern = /\([^)]+\)\/\([^)]+\)/g;

      // If line contains complex fractions, split and process them separately
      if (complexFractionPattern.test(line)) {
        const parts: (string | React.ReactElement)[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        // Reset the regex
        complexFractionPattern.lastIndex = 0;

        while ((match = complexFractionPattern.exec(line)) !== null) {
          const currentMatchIndex = match.index;

          // Add text before the match
          if (currentMatchIndex > lastIndex) {
            const textBefore = line.substring(lastIndex, currentMatchIndex);
            // Process text before for other math expressions (like subscripts)
            const beforeWords = textBefore.split(/(\s+)/);
            beforeWords.forEach((word, j) => {
              if (word.match(/^\s+$/)) {
                parts.push(word);
              } else if (containsFractions(word)) {
                const mathWord = formatMathExpression(word);
                parts.push(<InlineMath key={`before-${index}-${currentMatchIndex}-${j}`} math={mathWord} />);
              } else {
                parts.push(word);
              }
            });
          }

          // Add the formatted math expression
          const mathExpr = formatMathExpression(match[0]);
          parts.push(<BlockMath key={`math-${index}-${currentMatchIndex}`} math={mathExpr} />);

          lastIndex = currentMatchIndex + match[0].length;
        }

        // Add any remaining text
        if (lastIndex < line.length) {
          const remainingText = line.substring(lastIndex);
          // Process the remaining text word by word for other math expressions
          const remainingWords = remainingText.split(/(\s+)/);
          remainingWords.forEach((word, j) => {
            if (word.match(/^\s+$/)) {
              parts.push(word);
            } else if (containsFractions(word)) {
              const mathWord = formatMathExpression(word);
              parts.push(<InlineMath key={`remaining-${index}-${j}`} math={mathWord} />);
            } else {
              parts.push(word);
            }
          });
        }

        return (
          <div key={index} style={{ margin: '5px 0' }}>
            {parts}
          </div>
        );
      }

      // Otherwise, fall back to word-by-word processing
      const words = line.split(/(\s+)/); // Split by whitespace but keep the spaces
      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {words.map((word, i) => {
            // Skip whitespace
            if (word.match(/^\s+$/)) return word;

            // Check if this word contains mathematical expressions
            if (containsFractions(word)) {
              const mathWord = formatMathExpression(word);
              return <InlineMath key={i} math={mathWord} />;
            }
            return word;
          })}
        </div>
      );
    }

    // Check for coordinate pairs like (1, 2) or (-1, 3)
    if (line.match(/\(\s*-?\d+\s*,\s*-?\d+\s*\)/)) {
      const coordLine = line.replace(/\(\s*(-?\d+)\s*,\s*(-?\d+)\s*\)/g, '($1, $2)');
      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {coordLine.split(/(\([^)]+\))/).map((part, i) =>
            part.match(/^\([^)]+\)$/) ?
              <InlineMath key={i} math={part} /> :
              part
          )}
        </div>
      );
    }

    // Check for set notation like {1, 2, 3, ..., 10}
    if (line.match(/\{[^}]*\}/)) {
      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {line.split(/(\{[^}]*\})/).map((part, i) => {
            if (part.match(/^\{[^}]*\}$/)) {
              const mathPart = part.replace(/\{([^}]*)\}/, '\\{$1\\}').replace(/\.\.\./g, '\\ldots');
              return <InlineMath key={i} math={mathPart} />;
            }
            return part;
          })}
        </div>
      );
    }

    // Check for complex math expressions (equations with variables and operations)
    if (line.match(/[a-zA-Z][²₁₂₃]|[a-zA-Z]\^?\d|√\d/)) {
      const parts = line.split(/(\s+)/); // Split by whitespace but keep the spaces
      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {parts.map((part, i) => {
            // Skip whitespace
            if (part.match(/^\s+$/)) return part;

            // Check if this part contains math
            if (part.match(/[a-zA-Z][²₁₂₃]|[a-zA-Z]\^?\d|√\d/)) {
              const mathPart = formatMathExpression(part);
              return <InlineMath key={i} math={mathPart} />;
            }
            return part;
          })}
        </div>
      );
    }

    // Regular text - render as-is
    return (
      <div key={index} style={{ margin: '5px 0' }}>
        {line}
      </div>
    );
  });
};

// Renders a single answer-choice value, using inline math when the value
// contains math expressions (same condition as the scored test interface).
export const renderAnswerChoice = (value: string): React.ReactNode => {
  if (containsFractions(value) || value.match(/[a-zA-Z][²₁₂₃]|√\d|[a-zA-Z]\^?\d/)) {
    return <InlineMath math={formatMathExpression(value)} />;
  }
  return value;
};
